import { createTheme, alpha } from '@mui/material/styles';

const defaultTheme = createTheme();

export const brand = {
	50: 'hsl(210, 100%, 95%)',
	100: 'hsl(210, 100%, 92%)',
	200: 'hsl(210, 100%, 80%)',
	300: 'hsl(210, 100%, 65%)',
	400: 'hsl(210, 98%, 48%)',
	500: 'hsl(210, 98%, 42%)',
	600: 'hsl(210, 98%, 55%)',
	700: 'hsl(210, 100%, 35%)',
	800: 'hsl(210, 100%, 16%)',
	900: 'hsl(210, 100%, 21%)',
};

export const gray = {
	50: '#f8f9fa',
	100: '#e9ecef',
	200: '#dee2e6',
	300: '#ced4da',
	400: '#bfbfbf',
	500: '#6c757d',
	600: '#495057',
	700: '#343a40',
	800: '#212529',
	900: '#212529',
};

export const green = {
	50: 'hsl(120, 80%, 98%)',
	100: 'hsl(120, 75%, 94%)',
	200: 'hsl(120, 75%, 87%)',
	300: 'hsl(120, 61%, 77%)',
	400: 'hsl(120, 44%, 53%)',
	500: 'hsl(120, 59%, 30%)',
	600: 'hsl(120, 70%, 25%)',
	700: 'hsl(120, 75%, 16%)',
	800: 'hsl(120, 84%, 10%)',
	900: 'hsl(120, 87%, 6%)',
};

export const orange = {
	50: 'hsl(45, 100%, 97%)',
	100: 'hsl(45, 92%, 90%)',
	200: 'hsl(45, 94%, 80%)',
	300: 'hsl(45, 90%, 65%)',
	400: 'hsl(45, 90%, 40%)',
	500: 'hsl(45, 90%, 35%)',
	600: 'hsl(45, 91%, 25%)',
	700: 'hsl(45, 94%, 20%)',
	800: 'hsl(45, 95%, 16%)',
	900: 'hsl(45, 93%, 12%)',
};

export const red = {
	50: 'hsl(0, 100%, 97%)',
	100: 'hsl(0, 92%, 90%)',
	200: 'hsl(0, 94%, 80%)',
	300: 'hsl(0, 90%, 65%)',
	400: 'hsl(0, 90%, 40%)',
	500: 'hsl(0, 90%, 30%)',
	600: 'hsl(0, 91%, 25%)',
	700: 'hsl(0, 94%, 18%)',
	800: 'hsl(0, 95%, 12%)',
	900: 'hsl(0, 93%, 6%)',
	1000: '#ea0606',
};

export const colorSchemes = {
	light: {
		palette: {
			primary: {
				main: '#91D443',
				focus: '#91D443',
				contrastText: brand[50],
			},
			info: {
				main: '#91D443',
				focus: '#91D443',
				contrastText: gray[50],
			},
			logo: {
				main: '#2d326a',
				focus: '#2d326a',
			},
			mask: {
				main: '#91D443',
				focus: '#EEE9FFbb',
			},
			light: {
				main: '#e9ecef',
				focus: '#e9ecef',
			},
			secondary: {
				main: '#8392ab',
				focus: '#96a2b8',
			},
			grey: {
				...gray,
			},
			divider: alpha(gray[300], 0.4),
			background: {
				default: '#ffffff',
				paper: 'hsl(220, 35%, 97%)',
				contrast: '#070427',
				secondary: gray[600],
				footer: '#0a012b',
			},
			text: {
				primary: '#6e70b4',
				secondary: '#ffffff',
				title: '#2d326a',
				titleSecondary: '#2d326a',
				footer: '#696b96',
				agenda: '#6e70b4',
			},
			shadow: {
				main: '#91D443',
				focus: '#91D443',
			},
			action: {
				hover: alpha(gray[200], 0.2),
				selected: `${alpha(gray[200], 0.3)}`,
			},
			baseShadow: 'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
		},
	},
	dark: {
		palette: {
			primary: {
				main: '#91D443',
				focus: '#91D443',
				contrastText: brand[50],
			},
			info: {
				main: '#91D443',
				focus: '#91D443',
				contrastText: gray[50],
			},
			logo: {
				main: '#8d6dff',
				focus: '#EEE9FF',
			},
			mask: {
				main: '#91D443',
				focus: '#EEE9FFbb',
			},
			dark: {
				main: '#344767',
				focus: '#344767',
			},
			light: {
				main: '#e9ecef',
				focus: '#e9ecef',
			},
			secondary: {
				main: '#8392ab',
				focus: '#96a2b8',
				test: '#aea4f3',
			},
			grey: {
				...gray,
			},
			divider: alpha(gray[700], 0.6),
			background: {
				default: '#070427',
				paper: '#0d093c',
				contrast: '#ffffff',
				secondary: gray[800],
				footer: '#070427',
			},
			text: {
				primary: '#FFFFFF',
				secondary: '#6e70b4',
				contrast: '#ffffff',
				title: '#ffffff',
				titleSecondary: '#2d326a',
				footer: '#696b96',
				agenda: '#6e70b4',
			},
			shadow: {
				main: '#91D443',
				focus: '#91D443',
			},
			action: {
				hover: alpha(gray[600], 0.2),
				selected: alpha(gray[600], 0.3),
			},
			baseShadow: 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
		},
	},
};

export const typography = {
	fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
	h1: {
		fontSize: defaultTheme.typography.pxToRem(48),
		fontWeight: 400,
		lineHeight: 1.2,
		letterSpacing: -0.5,
		fontFamily: '"Saira", sans-serif',
	},
	h2: {
		fontSize: defaultTheme.typography.pxToRem(36),
		fontWeight: 400,
		lineHeight: 1.2,
		fontFamily: '"Saira", sans-serif',
	},
	h3: {
		fontSize: defaultTheme.typography.pxToRem(30),
		fontWeight: 400,
		lineHeight: 1.2,
		fontFamily: '"Saira", sans-serif',
	},
	h4: {
		fontSize: defaultTheme.typography.pxToRem(24),
		fontWeight: 400,
		lineHeight: 1.5,
		fontFamily: '"Saira", sans-serif',
	},
	h5: {
		fontSize: defaultTheme.typography.pxToRem(20),
		fontWeight: 400,
		fontFamily: '"Saira", sans-serif',
	},
	h6: {
		fontSize: defaultTheme.typography.pxToRem(18),
		fontWeight: 600,
		fontFamily: '"Saira", sans-serif',
	},
	h7: {
		fontSize: defaultTheme.typography.pxToRem(14),
		fontWeight: 600,
		fontFamily: '"Saira", sans-serif',
	},
	subtitle1: {
		fontSize: defaultTheme.typography.pxToRem(18),
		fontFamily: '"Saira", sans-serif',
	},
	subtitle2: {
		fontSize: defaultTheme.typography.pxToRem(14),
		fontWeight: 500,
		fontFamily: '"Saira", sans-serif',
	},
	body1: {
		fontSize: defaultTheme.typography.pxToRem(14),
	},
	body2: {
		fontSize: defaultTheme.typography.pxToRem(14),
		fontWeight: 400,
	},
	caption: {
		fontSize: defaultTheme.typography.pxToRem(12),
		fontWeight: 400,
	},
};

export const shape = {
	borderRadius: 8,
};

const defaultShadows = ['none', 'var(--template-palette-baseShadow)', ...defaultTheme.shadows.slice(2)];

export const shadows = defaultShadows;
